import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { CandyOffIcon, CheckIcon, MilkOffIcon, WheatOffIcon, ArrowRightIcon } from "lucide-react";
import { filterMetrics, tripleFilters, FilterId } from "../../data/tripleFilter";
import { desserts } from "../../data/images";
import { EASE_OUT } from "../../utils/motion";
const icons: Record<FilterId, typeof CandyOffIcon> = {
  sugar: CandyOffIcon,
  gluten: WheatOffIcon,
  lactose: MilkOffIcon
};
const R = 120;
const C = 2 * Math.PI * R;
const SEG = C / 3 - 14;
export function TripleFilterInteractive() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: "-120px"
  });
  const [active, setActive] = useState<Record<FilterId, boolean>>({
    sugar: false,
    gluten: false,
    lactose: false
  });
  const [focus, setFocus] = useState<FilterId>("sugar");
  const touched = useRef(false);
  useEffect(() => {
    if (!inView) return;
    const timers = tripleFilters.map((f, i) => setTimeout(() => {
      if (touched.current) return;
      setActive(a => ({
        ...a,
        [f.id]: true
      }));
      setFocus(f.id);
    }, 500 + i * 700));
    return () => timers.forEach(clearTimeout);
  }, [inView]);
  const toggle = (id: FilterId) => {
    touched.current = true;
    setActive(a => ({
      ...a,
      [id]: !a[id]
    }));
    setFocus(id);
  };
  const count = Object.values(active).filter(Boolean).length;
  const complete = count === 3;
  const focused = tripleFilters.find(f => f.id === focus)!;
  return <div ref={ref} className="grid items-center gap-10 rounded-[2rem] bg-white p-6 shadow-[0_30px_80px_-40px_rgba(59,34,25,0.5)] ring-1 ring-blush sm:p-10 lg:grid-cols-[auto_1fr] lg:gap-14" data-id="element-472" data-mp="4bp0132cfedp">
      {/* Ring visual */}
      <div className="relative mx-auto h-[280px] w-[280px]" data-id="element-473" data-mp="2k4e6uzmmzncn">
        <svg viewBox="0 0 280 280" className="absolute inset-0 h-full w-full" aria-hidden data-id="element-474" data-mp="2k5ywqby0ai46">
          {tripleFilters.map((f, i) => <g key={f.id} transform={`rotate(${-90 + i * 120 + 2.5} 140 140)`} data-id="element-475" data-mp="2hkhidllfylnj" data-mpi={i}>
              <circle cx="140" cy="140" r={R} fill="none" stroke="#F3DCD4" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${SEG} ${C - SEG}`} data-id="element-476" data-mp="2hidhlciua2rh" />
              <motion.circle cx="140" cy="140" r={R} fill="none" stroke={f.color} strokeWidth="14" strokeLinecap="round" strokeDasharray={`${SEG} ${C - SEG}`} initial={false} animate={{
            strokeDashoffset: active[f.id] ? 0 : SEG
          }} transition={{
            duration: 0.3,
            ease: EASE_OUT
          }} data-id="element-477" data-mp="27l8pyx685f3a" />
            </g>)}
        </svg>
        <div className="absolute inset-[34px] overflow-hidden rounded-full ring-4 ring-white" data-id="element-478" data-mp="1tv59ynxxuhma">
          <motion.img src={desserts.chocolateCake} alt="Torta de chocolate Triple Filtro" className="h-full w-full object-cover" animate={{
          filter: complete ? "saturate(1.1)" : `saturate(${0.3 + count * 0.25})`
        }} transition={{
          duration: 0.3
        }} data-id="element-479" data-mp="1fw5otq9mwkuv" />
          <div className="absolute inset-0 bg-cocoa/35" data-id="element-480" data-mp="wawzve1kydrd" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white" data-id="element-481" data-mp="1a93py80af9gh">
            <motion.span key={count} initial={{
            scale: 1.3,
            opacity: 0.5
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            duration: 0.25
          }} className="font-display text-5xl font-bold leading-none tabular" data-id="element-482" data-mp="158pf6i7axler">
              {count}/3
            </motion.span>
            <span className="mt-1 text-xs font-bold uppercase tracking-widest" data-id="element-483" data-mp="1szbmu8de93o8">filtros activos</span>
          </div>
        </div>
        <AnimatePresence data-id="element-484" data-mp="1oxhrmflywrvg">
          {complete && <motion.span initial={{
          opacity: 0,
          scale: 0.96,
          rotate: -18
        }} animate={{
          opacity: 1,
          scale: 1,
          rotate: -8
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.25,
          ease: EASE_OUT
        }} className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-xl bg-cocoa px-4 py-2 font-display text-base font-bold text-gold-light shadow-xl" data-id="element-485" data-mp="1p03yse4y55d5">
              Postre Triple Filtro
            </motion.span>}
        </AnimatePresence>
      </div>

      <div data-id="element-486" data-mp="2vqmc93m5jzqq">
        <p className="text-sm font-semibold text-cocoa-light" data-id="element-487" data-mp="2vr5t0h02gi36">Toca cada filtro y mira lo que pasa con tu postre:</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-488" data-mp="1z3rh8x5cx9bh">
          {tripleFilters.map(f => {
          const Icon = icons[f.id];
          const on = active[f.id];
          return <motion.button key={f.id} type="button" onClick={() => toggle(f.id)} aria-pressed={on} whileTap={{
            scale: 0.96
          }} className={`flex items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-gold-light ${on ? "text-white shadow-lg" : "border-blush-dark bg-cream text-cocoa hover:border-cocoa/40"}`} style={on ? {
            backgroundColor: f.color,
            borderColor: f.color
          } : undefined} data-id="element-489" data-mp="284fuvqqu97nu">
                <Icon className="h-6 w-6 shrink-0" data-id="element-490" data-mp="y54w1guosvlr" />
                <span className="flex-1 text-sm font-bold leading-tight" data-id="element-491" data-mp="y2irlq33ew7r">{f.label}</span>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${on ? "bg-white" : "border-2 border-blush-dark"}`} data-id="element-492" data-mp="u0ot9ogde4ar">
                  {on && <CheckIcon className="h-3.5 w-3.5" strokeWidth={3} style={{
                color: f.color
              }} data-id="element-493" data-mp="k5n4wutx5698" />}
                </span>
              </motion.button>;
        })}
        </div>

        <AnimatePresence mode="wait" data-id="element-494" data-mp="2eiv5720nya29">
          <motion.div key={focused.id + String(active[focused.id])} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.18,
          ease: EASE_OUT
        }} className="mt-5 rounded-2xl bg-blush-light p-4" data-id="element-495" data-mp="2ehttcvbtpjjh">
            <p className="text-sm font-bold" style={{
            color: focused.color
          }} data-id="element-496" data-mp="3u1uem5k7luvv">
              {focused.label}: {active[focused.id] ? "lo reemplazamos por" : "actívalo para ver los reemplazos"}
            </p>
            {active[focused.id] && <>
                <ul className="mt-2 flex flex-wrap gap-2" data-id="element-497" data-mp="qlneg9106fru">
                  {focused.swaps.map(s => <li key={s} className="flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm font-semibold text-cocoa shadow-sm" data-id="element-498" data-mp="vhugvru6x951">
                      <ArrowRightIcon className="h-3 w-3 text-cocoa-light" data-id="element-499" data-mp="lkzl3gpaxmug" />
                      {s}
                    </li>)}
                </ul>
                <p className="mt-3 text-sm text-cocoa-light" data-id="element-500" data-mp="36c02xrbmmcd7">{focused.benefit}</p>
              </>}
          </motion.div>
        </AnimatePresence>

        <dl className="mt-6 space-y-3" data-id="element-501" data-mp="2yqw6oe8agbhf">
          {filterMetrics.map(m => {
          const removed = (Object.keys(active) as FilterId[]).reduce((sum, id) => sum + (active[id] ? m.cut[id] : 0), 0);
          const value = m.kind === "good" ? 100 : Math.max(4, 100 - removed);
          return <div key={m.label} className="grid grid-cols-[130px_1fr_44px] items-center gap-3 text-sm sm:grid-cols-[150px_1fr_44px]" data-id="element-502" data-mp="33sbsw9m7co1e">
                <dt className="font-semibold text-cocoa" data-id="element-503" data-mp="2b2wf5c35kh1s">{m.label}</dt>
                <dd className="relative h-3 overflow-hidden rounded-full bg-blush" data-id="element-504" data-mp="2b0s48a6u6hs0">
                  <motion.span className={`absolute inset-y-0 left-0 rounded-full ${m.kind === "good" ? "bg-leaf" : "bg-berry"}`} initial={false} animate={{
                width: `${value}%`
              }} transition={{
                duration: 0.3,
                ease: EASE_OUT
              }} data-id="element-505" data-mp="2ptc3v0us274p" />
                </dd>
                <span className={`text-right font-bold tabular ${m.kind === "good" ? "text-leaf" : "text-berry"}`} data-id="element-506" data-mp="16tbvm24z01io">{value}%</span>
              </div>;
        })}
        </dl>
        <p className="mt-4 text-xs text-cocoa-light" data-id="element-507" data-mp="1gr9jupmtxyba">
          Gráfico ilustrativo. Lo que baja: lo que te inflama. Lo que se queda al 100%: el sabor y la textura.
        </p>
      </div>
    </div>;
}